import { useState, useEffect, useCallback } from 'react'
import { ICategory } from '@/app/[locale]/_types'

interface IOffsetTops {
  id: string
  offsetTop: number
  offsetHeight: number
  parentId?: string
}

const SCROLL_CONFIG = {
  DIFF_THRESHOLD: 1000,
  CHILD_CATEGORY_OFFSET: 200,
  PARENT_CATEGORY_OFFSET: 80,
  TIMEOUTS: {
    MANUAL_SCROLL_SHORT: 200,
  },
} as const

interface UseActiveCategoryProps {
  data: ICategory[]
  categoriesOffsetTops: IOffsetTops[]
  scrollPosition: number
  isManualScroll: boolean
  instanceRef: React.RefObject<any>
}

export const useActiveCategory = ({
  data,
  categoriesOffsetTops,
  scrollPosition,
  isManualScroll,
  instanceRef,
}: UseActiveCategoryProps) => {
  const [activeCategory, setActiveCategory] = useState<string>('')
  const [isFixedCatalog, setFixedCatalog] = useState(false)

  const isChildCategory = useCallback(
    (slug: string): boolean => {
      return data.some((category) =>
        category.child_categories?.some((child) => child.slug === slug)
      )
    },
    [data]
  )

  // Handle URL hash on initial load
  useEffect(() => {
    const hash = window.location.hash?.replace('#', '')
    if (hash && data.some((category) => category.slug === hash)) {
      setActiveCategory(hash)

      const scrollToElement = () => {
        const element = document.getElementById(hash)
        if (element) {
          const elementPosition =
            element.getBoundingClientRect().top + window.pageYOffset
          window.scrollTo({
            top:
              elementPosition -
              (isChildCategory(hash)
                ? SCROLL_CONFIG.CHILD_CATEGORY_OFFSET
                : SCROLL_CONFIG.PARENT_CATEGORY_OFFSET),
            behavior: 'smooth',
          })
        }
      }

      // Wait for offsets to be calculated
      const timeoutId = setTimeout(scrollToElement, 200)

      return () => clearTimeout(timeoutId)
    }
  }, [data, isChildCategory])

  // Update fixed catalog and active category based on scroll
  useEffect(() => {
    if (!instanceRef?.current || !categoriesOffsetTops.length) return

    const instanceBottom =
      instanceRef.current.offsetTop + instanceRef.current.offsetHeight
    setFixedCatalog(scrollPosition > instanceBottom)

    if (!isManualScroll) {
      let activeCat = ''
      let bestMatchOffset = -Infinity

      for (const { id, offsetTop, parentId } of categoriesOffsetTops) {
        const diff = scrollPosition - offsetTop
        if (
          diff >= 0 &&
          diff < SCROLL_CONFIG.DIFF_THRESHOLD &&
          offsetTop > bestMatchOffset
        ) {
          bestMatchOffset = offsetTop
          activeCat = id || parentId || ''
        }
      }

      if (activeCat && activeCat !== activeCategory) {
        setActiveCategory(activeCat)
      }
    }
  }, [
    scrollPosition,
    categoriesOffsetTops,
    instanceRef,
    activeCategory,
    isManualScroll,
  ])

  const setActiveCategoryManual = useCallback((slug: string) => {
    setActiveCategory(slug)
  }, [])

  const scrollToCategory = useCallback(
    (slug: string) => {
      const element = document.getElementById(slug)
      if (!element) return

      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top:
          elementPosition -
          (isChildCategory(slug)
            ? SCROLL_CONFIG.CHILD_CATEGORY_OFFSET
            : SCROLL_CONFIG.PARENT_CATEGORY_OFFSET),
        behavior: 'smooth',
      })
    },
    [isChildCategory]
  )

  return {
    activeCategory,
    isFixedCatalog,
    setActiveCategoryManual,
    scrollToCategory,
    isChildCategory,
  }
}
